import { Injectable, Injector, inject, signal, OnDestroy } from '@angular/core';
import { UnsavedWorkPromptService } from '@branduals/dialog-guard';

const VERSION_POLL_INTERVAL_MS = 300_000; // 5 minutes

/**
 * The page carries the WIRE EPOCH as a meta tag — `<meta name="branduals-wire-epoch" content="N">`.
 * The value lives in the page alone, never in a compiled constant (a stale bundle would carry its
 * own along), and is raised with every deploy whose wire a bundle built before it cannot speak:
 * a removed or renamed field, type or argument, a tightened nullability, a new required argument.
 */
const WIRE_EPOCH_MARKER_NAME = 'branduals-wire-epoch';
const WIRE_EPOCH_IN_PAGE = /<meta\s+name="branduals-wire-epoch"\s+content="(\d+)"/;
/** A page with no marker was built before the marker existed: the first epoch. */
const FIRST_WIRE_EPOCH = 1;

/**
 * Polls /index.html to detect new app deployments.
 *
 * On startup, fetches /index.html and stores a hash of the response. Every 5 minutes, re-fetches
 * and compares, reading the page's wire epoch beside the hash:
 * - a HIGHER epoch than the document this tab loaded with means the server no longer speaks to
 *   this bundle — `updateRequired` goes true, the banner offers Refresh alone, and polling ends
 *   (there is nothing more to learn);
 * - a changed hash on the same epoch is an ordinary update — `updateAvailable` goes true, the
 *   banner can be dismissed, and polling CONTINUES so a later epoch is still seen.
 *
 * - Dismiss: hides the ordinary banner and remembers the page it was shown for — the same page does
 *   not bring it back, a different one does, and a higher epoch still requires the refresh.
 * - Refresh: calls location.reload() to pick up the new index.html and chunks.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService implements OnDestroy {
  readonly updateAvailable = signal(false);
  readonly updateRequired = signal(false);

  private startupHash = '';
  private latestHash = '';
  private dismissedHash: string | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  // Lazy — the leave prompt is needed only when the member clicks Refresh, and an eager
  // inject would drag its dependency graph into every consumer of this root service.
  private readonly injector = inject(Injector);

  constructor() {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  refresh(): void {
    // The refresh banner invites a reload while the member may be mid-edit — the in-app leave
    // prompt is the honest voice (the browser's native prompt would fire anyway, wordless);
    // an approved leave then passes the native prompt's first evaluation too.
    void this.injector.get(UnsavedWorkPromptService).confirmLeaveIfDirty(() => {
      location.reload();
    });
  }

  dismiss(): void {
    // Only the ordinary banner can be dismissed: a required refresh stays until the member refreshes.
    if (this.updateRequired()) return;
    this.dismissedHash = this.latestHash;
    this.updateAvailable.set(false);
  }

  private async initialize(): Promise<void> {
    const page = await this.fetchIndex();
    if (page) {
      this.startupHash = page.hash;
      this.latestHash = page.hash;
      this.intervalId = setInterval(() => this.checkForUpdate(), VERSION_POLL_INTERVAL_MS);
    }
  }

  private async checkForUpdate(): Promise<void> {
    const page = await this.fetchIndex();
    if (!page) return;
    this.latestHash = page.hash;
    if (page.epoch > this.runningEpoch()) {
      this.updateRequired.set(true);
      this.updateAvailable.set(false);
      this.stopPolling();
      return;
    }
    if (page.hash !== this.startupHash && page.hash !== this.dismissedHash) {
      this.updateAvailable.set(true);
    }
  }

  /** The epoch of the document this tab loaded with, read from its own head; no marker = the first epoch. */
  private runningEpoch(): number {
    return parseEpoch(document.querySelector(`meta[name="${WIRE_EPOCH_MARKER_NAME}"]`)?.getAttribute('content'));
  }

  /**
   * Uses raw fetch() instead of Angular's HttpClient — bypasses the auth
   * interceptor chain (which would add JWT tokens) and the API base URL
   * rewriter (which would redirect /index.html to the backend API).
   * This is a static file fetch, not an API call.
   */
  private async fetchIndex(): Promise<{ hash: string; epoch: number } | null> {
    try {
      const response = await fetch('/index.html', { cache: 'no-store' });
      const text = await response.text();
      return { hash: this.hash(text), epoch: parseEpoch(WIRE_EPOCH_IN_PAGE.exec(text)?.[1]) };
    } catch {
      return null; // Network error — skip this cycle
    }
  }

  private stopPolling(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private hash(str: string): string {
    // DJB2 hash — fast, non-crypto, sufficient for same-or-different comparison
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
      h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
    }
    return h.toString(36);
  }
}

function parseEpoch(value: string | null | undefined): number {
  const n = value === null || value === undefined ? Number.NaN : Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : FIRST_WIRE_EPOCH;
}
